// apps/web/src/routes/$lang/_layout/category.$category.tsx
import { createFileRoute, redirect } from "@tanstack/react-router";
import { transportCategoryRedirect } from "../../../features/transport/lib/hub-redirect";
import { isStayPropertyType } from "@majourneys/shared";
import { ExplorePage } from "../../../features/activities/pages/explore-page";
import { StaysTypePage } from "../../../features/stays/pages/stays-type-page";
import { ALL_STAY_TYPES, stayTypeQueryOptions } from "../../../features/stays/api/stays-query";
import { parseChildAges } from "../../../features/stays/lib/nights";
import {
  activitiesQueryOptions,
  buildActivityFilters,
  type ActivityRouteSearch,
} from "../../../features/activities/api/activities-query";
import {
  categoryBySlugQueryOptions,
  cityBySlugQueryOptions,
} from "../../../features/activities/hooks/use-seo-lookup";
import { ensureHubSlugsExist } from "../../../features/activities/api/hub-guard";
import { categoriesQueryOptions } from "../../../features/activities/hooks/use-categories";
import { citiesQueryOptions } from "../../../features/activities/hooks/use-cities";

/**
 * The activity params this hub already had, plus the four a hotel list needs.
 *
 * They share one route because they are one page to a guest: /category/camp
 * and /category/hotel are both "a place to stay", reached from the same chip
 * row. Only the source of the rows differs — camps come from the activities
 * catalogue, everything charged online from the supplier — and that fork is
 * made once, below.
 */
export interface CategorySearch extends ActivityRouteSearch {
  checkin?: string;
  checkout?: string;
  adults?: number;
  childAges?: string;
  city?: string;
}

/**
 * `hotel`, `riad`, `resort` and `villa` are property types, and the properties
 * behind them are charged online in full at booking. Camps are paid online
 * too, from a link our team emails; activities are paid on arrival.
 *
 * Only `villa` is also a row in `categories`, left over from the partner
 * villas we no longer sell. This fork wins over that row on purpose: the
 * category holds nothing published, and a guest asking for villas wants the
 * ones we can actually book.
 *
 * That difference is real, and the listing states it above its first card.
 * But it was never a reason to move the guest to a different URL family
 * mid-browse, which is what /stays/{type} did: clicking "Hotels" in a row of
 * chips read as being thrown off the page. So the URL shape holds and only
 * the data source forks.
 */
function isStayTypeHub(category: string): boolean {
  return isStayPropertyType(category);
}

function CategoryRoutePage() {
  const { category } = Route.useParams();
  return isStayTypeHub(category) ? <StaysTypePage /> : <ExplorePage />;
}

export const Route = createFileRoute("/$lang/_layout/category/$category")({
  // Transfers are booked on the transport hub only; the category page 301s there.
  beforeLoad: ({ params }) => {
    const hub = transportCategoryRedirect(params.category, params.lang);
    if (hub) throw redirect(hub);
  },
  // See place.$city.tsx — prefetch so navigation keeps the old page visible
  // rather than flashing the full-page Suspense spinner.
  loaderDeps: ({ search }: { search: CategorySearch }) => ({
    q: search.q,
    page: search.page,
    min_price: search.min_price,
    max_price: search.max_price,
    sort: search.sort,
    show: search.show,
    tag: search.tag,
    cuisine: search.cuisine,
    // The booking bar and the "Open now" / "Tables tonight" chips filter the
    // list, so they key the prefetch too (see buildActivityFilters).
    date: search.date,
    time: search.time,
    open: search.open,
    tonight: search.tonight,
    checkin: search.checkin,
    checkout: search.checkout,
    adults: search.adults,
    childAges: search.childAges,
  }),
  loader: async ({ context, params, deps }) => {
    // An unknown category is a 404, not an empty page (see hub-guard.ts).
    await ensureHubSlugsExist(context.queryClient, { category: params.category });
    /**
     * The undated "All" list the Stays row counts its Hotels / Riads / Villas
     * tiles from. Warmed with the page so the tiles are in the first paint;
     * fetched in the browser they arrived about four seconds late and the row
     * showed only All and Camp until then (owner, 2026-09-26). Cached five
     * minutes on the API, so this is usually instant.
     */
    const warmStayTiles = () =>
      context.queryClient.ensureQueryData(stayTypeQueryOptions(ALL_STAY_TYPES, params.lang, {})).catch(() => undefined);
    if (isStayTypeHub(params.category)) {
      /**
       * Without this the crawler — and the guest's first paint — gets an empty
       * grid while the supplier call runs on the client.
       *
       * childAges is parsed to the shape the page itself uses (undefined,
       * never [], when there are no children) because the query key folds it
       * in: [] and undefined stringify differently, so an empty array warms a
       * key the page never reads and the paid supplier lookup runs twice.
       */
      const options = stayTypeQueryOptions(params.category, params.lang, {
        checkin: deps.checkin,
        checkout: deps.checkout,
        adults: deps.adults,
        childAges: deps.childAges ? parseChildAges(deps.childAges) : undefined,
      });
      /**
       * Awaited only on the server, for the crawler and the first paint. In the
       * browser an awaited loader holds the OLD page on screen until the
       * supplier answers: a guest changed 3 guests to 4, pressed search, and
       * watched the old results and "3 voyageurs" sit there for about eight
       * seconds with no sign anything was happening (owner, 2026-09-26). Not
       * awaiting lets the page switch at once and show its "Finding your
       * stay" loader while the same fetch runs.
       */
      if (typeof window === "undefined") {
        await Promise.all([context.queryClient.ensureQueryData(options), warmStayTiles()]);
      } else {
        // The page's own query reports a failure; this warm-up must not
        // surface it a second time as an unhandled rejection.
        void context.queryClient.ensureQueryData(options).catch(() => {});
        void warmStayTiles();
      }
      return;
    }

    if (params.category === "stays" && typeof window !== "undefined") void warmStayTiles();
    const filters = buildActivityFilters(params, deps);
    await Promise.all([
      // Same rule as above: awaited on the server only.
      ...(params.category === "stays" && typeof window === "undefined" ? [warmStayTiles()] : []),
      context.queryClient.ensureQueryData(activitiesQueryOptions(filters)),
      context.queryClient.ensureQueryData(cityBySlugQueryOptions(undefined)),
      context.queryClient.ensureQueryData(categoryBySlugQueryOptions(params.category)),
      // The cross-links read these to link only hubs with something in them.
      context.queryClient.ensureQueryData(categoriesQueryOptions(undefined, params.lang)),
      context.queryClient.ensureQueryData(citiesQueryOptions(params.lang)),
    ]);
  },
  component: CategoryRoutePage,
});
